"use client";

import * as React from "react";
import { DISCORD_INVITE_URL, TWITTER_URL, TELEGRAM_URL } from "@cede/utils";
import { XIcon, TelegramIcon, DiscordIcon } from "../library/social";
import { cn } from "@/lib/utils";

export interface SocialLinksProps {
  /**
   * Which social platforms to display
   * @default ["discord", "x", "telegram"]
   */
  platforms?: Array<"discord" | "x" | "telegram">;

  /**
   * Icon size
   * @default "md"
   */
  size?: "sm" | "md" | "lg" | "xl" | number;

  /**
   * Display variant
   * @default "default"
   */
  variant?: "default" | "primary" | "secondary";

  /**
   * Show icons with underlay background
   * @default false
   */
  withUnderlay?: boolean;

  /**
   * Custom underlay colors for each platform
   */
  underlayColors?: {
    discord?: string;
    x?: string;
    telegram?: string;
  };

  /**
   * Enable interactive hover effects
   * @default true
   */
  interactive?: boolean;

  /**
   * Display orientation
   * @default "horizontal"
   */
  orientation?: "horizontal" | "vertical";

  /**
   * Gap between icons (Tailwind spacing)
   * @default "gap-3"
   */
  gap?: string;

  /**
   * Additional className for container
   */
  className?: string;

  /**
   * Open links in new tab
   * @default true
   */
  openInNewTab?: boolean;

  /**
   * Custom click handlers (overrides default URL navigation)
   */
  onPlatformClick?: {
    discord?: () => void;
    x?: () => void;
    telegram?: () => void;
  };
}

const PLATFORM_CONFIG = {
  discord: {
    Icon: DiscordIcon,
    url: DISCORD_INVITE_URL,
    label: "Discord",
  },
  x: {
    Icon: XIcon,
    url: TWITTER_URL,
    label: "X (Twitter)",
  },
  telegram: {
    Icon: TelegramIcon,
    url: TELEGRAM_URL,
    label: "Telegram",
  },
} as const;

/**
 * Social media links composition component
 *
 * Provides a clean, consistent way to display social media icons with links.
 * Replaces the old MediaIconsGroup with better flexibility and modern primitives.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <SocialLinks />
 *
 * // Custom platforms (all three)
 * <SocialLinks platforms={["discord", "x", "telegram"]} />
 *
 * // With underlay backgrounds
 * <SocialLinks
 *   withUnderlay
 *   size="lg"
 * />
 *
 * // Vertical orientation
 * <SocialLinks
 *   orientation="vertical"
 *   gap="gap-2"
 * />
 *
 * // Custom colors
 * <SocialLinks
 *   withUnderlay
 *   underlayColors={{
 *     discord: "bg-purple-500",
 *     x: "bg-gray-900",
 *   }}
 * />
 *
 * // Custom click handlers
 * <SocialLinks
 *   onPlatformClick={{
 *     discord: () => console.log("Discord clicked!"),
 *     x: () => console.log("X clicked!"),
 *   }}
 * />
 * ```
 */
export const SocialLinks = React.forwardRef<HTMLDivElement, SocialLinksProps>(
  (
    {
      platforms = ["discord", "x", "telegram"],
      size = 32,
      variant = "default",
      withUnderlay = true,
      interactive = true,
      orientation = "horizontal",
      gap = "gap-3",
      className,
      openInNewTab = true,
      onPlatformClick,
    },
    ref,
  ) => {
    const handleClick = (platform: keyof typeof PLATFORM_CONFIG) => {
      // Use custom handler if provided
      if (onPlatformClick?.[platform]) {
        onPlatformClick[platform]();
        return;
      }

      // Default: open URL
      const config = PLATFORM_CONFIG[platform];
      if (openInNewTab) {
        window.open(config.url, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = config.url;
      }
    };

    return (
      <div
        ref={ref}
        className={cn("flex items-center", orientation === "horizontal" ? gap : `flex-col ${gap}`, className)}
        role="list"
        aria-label="Social media links"
      >
        {platforms.map((platform) => {
          const config = PLATFORM_CONFIG[platform];
          const Icon = config.Icon;

          return (
            <button
              key={platform}
              onClick={() => handleClick(platform)}
              className={cn(
                "inline-flex items-center justify-center rounded-full",
                "transition-transform duration-200",
                interactive && "hover:scale-110 active:scale-95",
              )}
              aria-label={`Visit our ${config.label}`}
              role="listitem"
            >
              <Icon size={size} variant={variant} withUnderlay={withUnderlay} interactive={interactive} />
            </button>
          );
        })}
      </div>
    );
  },
);

SocialLinks.displayName = "SocialLinks";
